/**
 * Static prototype file service — backed by the in-memory mock store.
 *
 * Uploads are simulated (metadata only; no bytes leave the browser). Downloads
 * point at the static template documents bundled in /public/templates. The
 * static helper API (validate/format/icon/label) is unchanged.
 */

import { FileResponseDto, SectionType } from '../types';
import { db, delay, newId, persist } from '../mocks/store';
import { filesForApp, toFileResponseDto } from '../mocks/mappers';

// Error class for file service operations
export class FileServiceError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = 'FileServiceError';
  }
}

// Interface for upload progress callback
export interface UploadProgressCallback {
  (progress: number): void;
}

// Pick a plausible static template to "download" based on section/type.
const fallbackDownloadUrl = (sectionType: SectionType, mimeType: string): string => {
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return '/templates/Presupuesto_AvanzaUC2025.xlsx';
  }
  if (sectionType === 'PROJECT_DESCRIPTION') {
    return '/templates/Avanza_UC_Formulacion Proyecto.docx';
  }
  return '/templates/CARTA APOYO SPA.docx';
};

export class FileService {
  /**
   * Upload a file (simulated — stores metadata in the mock store).
   */
  static async uploadFile(
    file: File,
    applicationId: string,
    sectionType: SectionType,
    onProgress?: UploadProgressCallback
  ): Promise<FileResponseDto> {
    // Simulate upload progress.
    if (onProgress) {
      for (const p of [20, 55, 85, 100]) {
        await delay(null, 80);
        onProgress(p);
      }
    } else {
      await delay(null, 250);
    }

    const stored = {
      id: newId('file'),
      applicationId,
      sectionType,
      filename: `${Date.now()}_${file.name}`,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      uploadedAt: new Date().toISOString(),
      downloadUrl: fallbackDownloadUrl(sectionType, file.type || ''),
    };
    db.files.push(stored);
    persist();
    return toFileResponseDto(stored);
  }

  /**
   * Upload multiple files sequentially
   */
  static async uploadMultipleFiles(
    files: File[],
    applicationId: string,
    sectionType: SectionType,
    onProgress?: (fileIndex: number, progress: number, fileName: string) => void
  ): Promise<FileResponseDto[]> {
    const results: FileResponseDto[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const result = await this.uploadFile(
        file,
        applicationId,
        sectionType,
        onProgress ? (progress) => onProgress(i, progress, file.name) : undefined
      );
      results.push(result);
    }
    return results;
  }

  /**
   * Get all files for an application
   */
  static async listApplicationFiles(
    applicationId: string
  ): Promise<FileResponseDto[]> {
    return delay(filesForApp(applicationId));
  }

  /**
   * Delete a file
   */
  static async deleteFile(fileId: string): Promise<void> {
    const idx = db.files.findIndex((f) => f.id === fileId);
    if (idx !== -1) {
      db.files.splice(idx, 1);
      persist();
    }
    return delay(undefined, 150);
  }

  /**
   * Get file metadata by ID
   */
  static async getFileMetadata(
    fileId: string,
    _applicationId?: string
  ): Promise<FileResponseDto | null> {
    const file = db.files.find((f) => f.id === fileId);
    return delay(file ? toFileResponseDto(file) : null, 100);
  }

  /**
   * Download a file with proper filename (gets metadata first if needed)
   */
  static async downloadFileWithMetadata(
    fileId: string,
    _applicationId?: string,
    fallbackFilename?: string
  ): Promise<void> {
    const file = db.files.find((f) => f.id === fileId);
    const filename = file?.originalName || fallbackFilename || `file_${fileId}`;
    this.downloadFile(fileId, filename);
  }

  /**
   * Download a file (trigger browser download of the static template).
   */
  static downloadFile(fileId: string, filename: string): void {
    const file = db.files.find((f) => f.id === fileId);
    const href = file?.downloadUrl || '/templates/Avanza_UC_Formulacion Proyecto.docx';
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Get download URL for a file
   */
  static getDownloadUrl(fileId: string): string {
    const file = db.files.find((f) => f.id === fileId);
    return file?.downloadUrl ?? '#';
  }

  /**
   * Validate file before upload
   */
  static validateFile(
    file: File,
    acceptedTypes?: string[],
    maxFileSize: number = 15 * 1024 * 1024 // 15MB default
  ): { valid: boolean; error?: string } {
    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      return {
        valid: false,
        error: `El archivo es demasiado grande. Máximo permitido: ${maxSizeMB}MB`,
      };
    }

    if (acceptedTypes && acceptedTypes.length > 0) {
      const isTypeAccepted = acceptedTypes.some((type) => {
        if (type.includes('/')) {
          return file.type === type;
        }
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      });

      if (!isTypeAccepted) {
        const extensions = acceptedTypes
          .filter((type) => !type.includes('/'))
          .join(', ');
        return {
          valid: false,
          error: `Tipo de archivo no permitido. Tipos permitidos: ${extensions}`,
        };
      }
    }

    return { valid: true };
  }

  /**
   * Validate multiple files
   */
  static validateMultipleFiles(
    files: File[],
    acceptedTypes?: string[],
    maxFileSize: number = 15 * 1024 * 1024,
    maxFiles?: number
  ): { valid: boolean; errors: Array<{ file: File; error: string }> } {
    const errors: Array<{ file: File; error: string }> = [];

    if (maxFiles && files.length > maxFiles) {
      return {
        valid: false,
        errors: [{ file: files[0], error: `Máximo ${maxFiles} archivos permitidos` }],
      };
    }

    files.forEach((file) => {
      const validation = this.validateFile(file, acceptedTypes, maxFileSize);
      if (!validation.valid && validation.error) {
        errors.push({ file, error: validation.error });
      }
    });

    return { valid: errors.length === 0, errors };
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on MIME type
   */
  static getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  }

  /**
   * Get file type label for display
   */
  static getFileTypeLabel(mimeType: string): string {
    const typeMap: Record<string, string> = {
      'application/pdf': 'PDF',
      'application/msword': 'Word',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
      'application/vnd.ms-excel': 'Excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
      'image/jpeg': 'JPEG',
      'image/png': 'PNG',
    };
    return typeMap[mimeType] || 'Archivo';
  }
}

export default FileService;
